import styled, { css } from 'styled-components'

import { Button, ButtonIconWrapper, Text } from '@sketch/components'
import { ReactComponent as BellIconSvg } from '@sketch/icons/notification-24'

import { DASHED_SEPARATOR } from './constants'
import { breakpoint } from '@sketch/global-styles'

export const Root = styled.article`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  /* min-height: 0 lets this flex item shrink to its parent's height instead
   * of growing to its content. Without it, Root takes its content height and
   * overflow-y: auto has nothing to scroll — which in turn prevents the
   * sticky actions bar from engaging, since sticky anchors to this element. */
  min-height: 0;

  /* No align-items: center here — InboxContent centers itself via
   * margin: 0 auto, and forcing per-item cross-axis alignment makes
   * position: sticky on the actions bar fragile. */
  padding: 0px 10px;

  /* Only act as a scroll container on desktop, where the flex parent
   * (UpdatePreview) constrains this element's height. On mobile/tablet the
   * preview lives inside a Modal whose Container is the real scroll
   * surface — keep overflow visible here so the sticky actions bar
   * resolves to the modal's scroll viewport rather than to Root, which
   * never scrolls. Note: setting overflow on a single axis (e.g.
   * overflow-x hidden) would coerce the other axis to auto and re-promote
   * Root to a scroll container, so both axes stay visible below md. */
  ${breakpoint('md')`
    overflow-x: hidden;
    overflow-y: auto;
    padding: 0px 10px 23px;
  `}
`

export const ActionButton = styled(Button)`
  /* Don't let buttons shrink to fit the bar — they must keep their labelled
   * width so the bar overflows and the icon-only collapse can be detected. */
  flex-shrink: 0;

  ${ButtonIconWrapper} {
    width: 17px;
    height: 17px;
  }

  & + & {
    margin: 0;
  }
`

export const ActionsBar = styled.div`
  display: flex;
  /* Content is justified to the start so overflow spills toward the end (right),
   * where it's measurable via scrollWidth — flex-end would push overflow off the
   * start edge, which scrollWidth ignores. The bar itself is right-aligned via
   * margin-left: auto, so this doesn't change the fitting layout. */
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
  overflow-x: hidden;
  flex-shrink: 0;
  margin-left: auto;
  /* Cap at the available width so the bar never pushes past its parent; its
   * content overflows internally (clipped) instead, which the icon-only
   * collapse measurement detects via scrollWidth. */
  max-width: 100%;
`

/* Sticky wrapper around the actions bar. The backdrop lives here (not on the
 * bar itself) because the bar has overflow-x: hidden to drive its icon-only
 * collapse measurement — that would clip an in-bar pseudo. Relies on Root's
 * default cross-axis stretch (Root must NOT set align-items: center) so the
 * wrapper spans Root's content width, which is also what
 * InboxPreviewActions' collapse measurement reads as `parentElement`. */
export const StickyActions = styled.div<{ $scrolled?: boolean }>(
  ({ theme, $scrolled }) => css`
    position: sticky;
    top: 0;
    z-index: ${theme.zIndex[1]};

    display: flex;
    padding: 13px 0;

    /* Below the desktop breakpoint the preview lives inside a Modal whose
     * Container is the actual scroll surface. We flip the bar to the bottom
     * of the scroll viewport and use margin-top: auto so it lands at the
     * natural floor of Root's content even when the modal isn't scrolling. */
    ${breakpoint('base', 'md')`
      top: auto;
      bottom: 0;
      margin-top: auto;
    `}

    &::before {
      content: '';
      position: absolute;

      /* -10px on each side cancels Root's horizontal padding so the backdrop
       * bleeds edge-to-edge of the scroll container. Keep this inset in sync
       * if Root's horizontal padding changes. */
      inset: 0 -10px;
      z-index: -1;

      backdrop-filter: blur(15px);
      background-color: ${theme.colors.headerSticky.background};
      box-shadow: ${theme.colors.headerSticky.shadow};

      opacity: ${$scrolled ? 1 : 0};
      transition: opacity
        ${`${theme.transitions.duration[2]} ${theme.transitions.timing.easeInOut}`};
    }
  `
)

/**
 * Wraps each action button's text. Hidden when the action bar collapses to
 * icon-only mode (see InboxPreviewActions) so the buttons shrink to their
 * icons when there isn't room for every label in a single row.
 */
export const ActionLabel = styled.span`
  white-space: nowrap;

  [data-icon-only] & {
    display: none;
  }
`

export const ActionsDivider = styled.span`
  border-left: 1px dashed ${DASHED_SEPARATOR};
  height: 12px;
  display: block;
  margin: 0px 10px;
`

export const InboxContent = styled.div`
  margin: 20px auto 0;
  max-width: 596px;
  width: 100%;
  min-width: 0;

  ${breakpoint('md')`
    margin: 72px auto 0;
  `}
`

export const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 0 0 15px;
`

export const HeaderTitle = styled(Text).attrs({
  textStyle: 'copy.primary.standard.F',
})`
  margin: 0;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  min-width: 0;
`

export const RichPreviewBox = styled.div(
  ({ theme }) => css`
    flex: 1;
    min-height: 280px;
    border: 1px dashed ${theme.colors.border.A};
    border-radius: ${theme.radii.xlarge};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${theme.colors.foreground.secondary.D};
    padding: 24px;
  `
)

export const RichPreviewText = styled(Text).attrs({
  textStyle: 'copy.tertiary.standard.E',
})`
  margin: 0;
`

export const ReasonRow = styled.footer`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.foreground.secondary.D};
  margin-left: 17px;
`

export const ReasonIcon = styled(BellIconSvg)`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
`

export const ReasonText = styled(Text).attrs({
  textStyle: 'copy.tertiary.standard.D',
})`
  margin: 0;

  strong {
    color: ${({ theme }) => theme.colors.foreground.secondary.A};
    font-weight: ${({ theme }) => theme.fontWeights.medium};
  }
`

export const VerticalDivider = styled.span`
  border-left: 1px dashed ${DASHED_SEPARATOR};
  height: 12px;
  margin: 5px 11px 5px 29px;
  display: block;
`
